import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../Styles";
import { CheckBox, CheckStyles, WaitAnimation, NewButton } from "..";
import { ActivePensList } from "../LocationFeeder/SubComponents/activePensList";
import { useLfContext } from "../LocationFeeder/Modals/_Modals/lfPenContext";
import LF_PENLIST from "../LocationFeeder/SubComponents/lfPenList";

interface SystemControlsInner_Props {
  focus_view?: boolean;
  selectContent: JSX.Element
  button: {
    onClick: () => void;
    content: string;
    startEnhancer: keyof typeof glyphs;
    disabled: boolean
  }
}

export default function SystemControlsInner({ focus_view, selectContent, button }: SystemControlsInner_Props) {
  const { t } = useTranslation();
  const { state: {selectedSection, selectedPens, error}, dispatch } = useLfContext();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;

  const _fontSize = { ...(focus_view ? fonts.fontSize3 : fonts.fontSize4) }

  if (!selectedSection) return;
            
  const sectionPens = selectedSection.section_locations.map(loc => loc);

  const allSelected =
    sectionPens.every(penId => selectedPens?.includes(penId));

  return ( selectedSection ? (
    <div style={{ minHeight: mobile ? "100%" : undefined, height: mobile ? undefined : "100%", boxSizing: "border-box", gap: mobile ? "20px 0px" : "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "280px 2px 280px", gridTemplateRows: mobile ? "fit-content(100%) 2px fit-content(100%)" : "100%" }}>

      {/* Controls */}
      <div style={{ gridArea: "1/1/2/2", height: "100%", display: "grid", gap: "10px", gridTemplateColumns: "100%", gridTemplateRows: mobile ? "60px fit-content(100%) 250px 17px" : "50px fit-content(100%) 1fr 17px" }}>
        
        {/* Start Selected */}
        <div style={{ gridArea: "1/1/2/2", margin: "0 auto", boxSizing: "border-box", width: "100%" }}>
          <NewButton
            focus_view={focus_view}
            content={button.content}
            startEnhancer={glyphs[button.startEnhancer]({ size: focus_view ? "40px" : "35px" })}
            overrides={{
              baseStyles: { backgroundColor: colors.greySecondary, width: mobile ? "100%" : "280px" },
              hoverStyles: { backgroundColor: colors.greyDarkSecondary },
            }}
            onClick={button.onClick}
            disabled={button.disabled}
          />
        </div>

        {/* SelectContent */}
        <div style={{ gridArea: "2/1/3/2" }}>
          {selectContent}
        </div>

        {/* Active Pen List */}
        <div style={{ gridArea: "3/1/4/2", height: "100%", overflow: "hidden", boxSizing: "border-box", display: "flex", flexDirection: "column"}}>
          <p style={{ ...fonts.header, ..._fontSize, color: colors.greyDarkPrimary, width: "60px", padding: "0px 0px 2px 10px" }}>
            {`${t("systemControls.general.feeding", { defaultValue: "feeding" })}:`}
          </p>

          <div style={{ position: "relative", backgroundColor: colors.greyLightSecondary, borderRadius: "10px", padding: "5px 10px", height: "100%", overflow: "hidden" }}>
            {(selectedPens === undefined || selectedPens.length === 0) && 
              <p style={{ ..._fontSize, ...css.emptyPen, opacity: "0.5" }}> {`${t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })}!`} </p>
            }
            {selectedPens &&
              <div style={{ height: "100%", overflowY: "auto", scrollbarColor: colors.greyTertiary + colors.transparent, scrollbarWidth: "thin" }}>
                <ActivePensList
                  focus_view={focus_view}
                />
              </div>
            }

          </div>
        </div>

        {/* Message */}
        <div style={{ gridArea: "4/1/5/2", textAlign: "center", opacity: error ? "1" : "0", color: error.Elementid === 2 ? colors.systemGreen : colors.systemRed, ...fonts.header, transition: "opacity 0.3s ease-in-out", ..._fontSize }}>{error.errorText}</div>

      </div>

      {/* Seperator */}
      <div style={{ gridArea: mobile ? "2/1/3/2" : "1/2/2/3", borderLeft: mobile ? undefined : "2px solid " + colors.greyTertiary, borderBottom: mobile ? "2px solid " + colors.greyTertiary : undefined }} />

      {/* Pens */}
      <div style={{ gridArea: mobile ? "3/1/4/3" : "1/3/2/4", height: "100%", width: "100%", display: "grid", gap: "5px", gridTemplateRows: mobile ? "30px 2px 250px 2px 30px" : "22px 2px 1fr 2px 22px", gridTemplateColumns: "100%" }}>

        {/* Select Text */}
        <p style={{ gridArea: "1/1/2/2", ...fonts.paragraph, ..._fontSize, color: colors.greyDarkPrimary, textAlign: "center" }}> {`${t("systemControls.general.clickPen", { defaultValue: "Click every pen you want to select" })}:`} </p>

        {/* Seperator */}
        <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.greyLightSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />
        
        {/* Section */}
        <div style={{
          gridArea: "3/1/4/2",
          transition: "filter 0.3s ease-in-out",
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarColor: colors.greyTertiary + colors.transparent,
          scrollbarWidth: "thin",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start"
        }}>
          <div style={{display: "block", margin: "auto"}}>
            <LF_PENLIST
              key={selectedSection.section_id}
              focus_view={focus_view}
              section={selectedSection}
            />
          </div>
        </div>

        {/* Seperator */}
        <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid " + colors.greyLightSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />

        {/* Select All */}
        <div style={{ gridArea: "5/1/6/2", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px"}}>
          <p style={{ ...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4),  color: colors.greyDarkPrimary }}> {`${t("systemControls.general.orSelectAll", { defaultValue: "Or select all" })}:`} </p>
          <CheckBox
            focus_view={focus_view}
            checkStyle={CheckStyles.grey}
            onChange={() => {
              if (allSelected) { dispatch({ selectedPens: selectedPens?.filter(penId => !sectionPens.includes(penId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedPens ?? [], ...sectionPens]));
                dispatch({ selectedPens: newSelection });
              }
            }}
            isChecked={allSelected}
          />
        </div>

      </div>

    </div> ) : (<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ ..._fontSize, ...fonts.paragraph, color: colors.greyDarkPrimary }}>No sections</p><WaitAnimation /></div>)
  )
}

function ScrollButtons({fillColor, right="0px", height="100%"}: {fillColor: colors, right?: string, height?: string}) {
  return (
    <div style={{ position: "absolute", right: right, height: height, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <glyphs.Arrow rotate={270} fillColor={fillColor} size="15px" />
      <glyphs.Arrow rotate={90} fillColor={fillColor} size="15px"/>
    </div>
  );
}




  // useEffect(() => {
  //   if (!sections || sections.length === 0) return;
  
  //   const first = sections[0];
  
  //   if (!first?.section_locations) return;
  
  //   if (!selectedSection) {
  //     dispatch({
  //       selectedSection: first,
  //       pens: first.section_locations,
  //       selectedPens: first.section_locations,
  //       nextFeeding_LocationsToFeed: first.section_locations
  //     });
  //   }
  // }, [dispatch, sections, selectedSection]);

  // console.log("selectedSection", selectedSection)
  // console.log("pens", pens)
  // console.log("selectedPens", selectedPens)
  // console.log("nextFeeding_LocationsToFeed", nextFeeding_LocationsToFeed)